import type { ReactNode } from 'react';

/**
 * A search snippet, rendered without ever setting raw HTML.
 *
 * `ContractSearchHitSchema` says the snippet arrives escaped, with highlight
 * markers `<mark>`…`</mark>`, produced by the index — *"the front end must not
 * re-derive them"*. Both halves of that are load-bearing:
 *
 *  - **Not re-derived.** Re-highlighting in the browser would mean the UI shows
 *    a different match from the one `ts_headline` scored, which is precisely
 *    the drift FR-27's explainability requirement exists to prevent.
 *  - **Not `dangerouslySetInnerHTML`.** The string is escaped by the index
 *    today, but "trust the escaping upstream" is a decision that has to be
 *    re-verified on every future change to the snippet producer. Splitting on
 *    the two known markers and emitting React nodes means the *only* markup
 *    this component can produce is `<mark>`, whatever the payload contains, so
 *    no raw HTML is set anywhere in this application.
 *
 * The escaped entities the index emits (`&amp;`, `&lt;`, `&gt;`, `&quot;`,
 * `&#39;`) are decoded back to their characters, because React escapes on
 * output — leaving them would print `&amp;` literally on screen.
 */
const MARK_SPLIT = /(<mark>|<\/mark>)/g;

const ENTITIES: Readonly<Record<string, string>> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
};

function decodeEntities(text: string): string {
  // `&amp;` last is handled by the single pass: the regex matches whole
  // entities, so `&amp;lt;` decodes to `&lt;` and stops, which is correct.
  return text.replace(/&(?:amp|lt|gt|quot|#39|apos);/g, (entity) => ENTITIES[entity] ?? entity);
}

export function Snippet({ html }: { readonly html: string }) {
  const nodes: ReactNode[] = [];
  let highlighted = false;
  let key = 0;

  for (const piece of html.split(MARK_SPLIT)) {
    if (piece === '<mark>') {
      highlighted = true;
      continue;
    }
    if (piece === '</mark>') {
      highlighted = false;
      continue;
    }
    if (piece === '') continue;

    const text = decodeEntities(piece);
    key += 1;
    nodes.push(
      highlighted ? (
        <mark key={key}>{text}</mark>
      ) : (
        <span key={key}>{text}</span>
      ),
    );
  }

  return <span className="snip">{nodes}</span>;
}
